/**
 * Menções no campo de escrita.
 *
 * O servidor guarda menção como `<@id>`, que é o certo: o apelido de alguém
 * muda, o identificador não. Só que mostrar isso dentro do campo de escrita é
 * o mesmo que pedir para a pessoa revisar um número de dezoito dígitos no meio
 * da frase. Enquanto está escrevendo, ela vê o mesmo nome que vai aparecer na
 * mensagem publicada; a tradução para `<@id>` acontece na hora de enviar.
 *
 * O nome exibido é o apelido da pessoa naquele servidor, e não o nome de
 * usuário. Quem escreve reconhece quem está marcando pelo nome que vê na lista
 * de membros; o nome de usuário é um detalhe de cadastro que muita gente nem
 * sabe qual é.
 *
 * Um editor de conteúdo rico resolveria isso mostrando a menção como uma
 * etiqueta, mas trocar o `<textarea>` por `contenteditable` traz junto os
 * problemas de teclado asiático, colar com formatação e desfazer, que hoje
 * funcionam de graça.
 */

import type { GuildMember } from '@nexus/shared'

/** O que pode aparecer depois do arroba enquanto se digita o nome. */
const CARACTERE_DE_NOME = /[\p{L}\p{N}._-]/u

/**
 * Apelidos costumam ter nome e sobrenome, então o espaço faz parte do que se
 * digita. Passar de dois quer dizer, quase sempre, que o arroba lá atrás já não
 * tem relação com o que está sendo escrito agora.
 */
const ESPACOS_TOLERADOS = 2

export interface MencaoEmDigitacao {
  /** Posição do arroba no texto. */
  inicio: number
  /** O que já foi digitado depois dele, sem o arroba. */
  termo: string
}

/** Como a pessoa aparece na lista de membros deste servidor. */
export function rotuloDeMencao(membro: GuildMember): string {
  return membro.nickname ?? membro.user.displayName
}

/**
 * Descobre se o cursor está no meio de uma menção sendo digitada.
 *
 * O arroba só conta quando está no começo do texto ou logo depois de um espaço.
 * Sem essa regra, escrever um e-mail abriria a lista de membros no meio do
 * endereço.
 */
export function detectarMencao(texto: string, cursor: number): MencaoEmDigitacao | null {
  let posicao = cursor - 1
  let espacos = 0

  while (posicao >= 0) {
    const caractere = texto[posicao] ?? ''

    if (caractere === '@') {
      const anterior = posicao > 0 ? (texto[posicao - 1] ?? '') : ''
      if (anterior !== '' && !/\s/.test(anterior)) return null
      return { inicio: posicao, termo: texto.slice(posicao + 1, cursor) }
    }

    if (caractere === '\n') return null

    if (caractere === ' ') {
      espacos += 1
      if (espacos > ESPACOS_TOLERADOS) return null
    } else if (!CARACTERE_DE_NOME.test(caractere)) {
      return null
    }

    // Nome nenhum é tão comprido; parar aqui evita varrer a mensagem inteira a
    // cada tecla digitada.
    if (cursor - posicao > 48) return null
    posicao -= 1
  }

  return null
}

/** Troca a menção em digitação pelo nome escolhido e diz onde o cursor deve ficar. */
export function aplicarMencao(
  texto: string,
  mencao: MencaoEmDigitacao,
  cursor: number,
  nome: string,
): { texto: string; cursor: number } {
  const inserido = `@${nome} `
  return {
    texto: texto.slice(0, mencao.inicio) + inserido + texto.slice(cursor),
    cursor: mencao.inicio + inserido.length,
  }
}

/**
 * Ordena os membros para a lista de sugestões.
 *
 * Quem começa com o que foi digitado vem antes de quem só contém em algum
 * lugar: digitar "ga" e ver "Gabriel" no topo é o esperado, ter que passar por
 * "Tiago" antes não é.
 */
export function buscarMembrosParaMencao(
  membros: GuildMember[],
  termo: string,
  limite = 8,
): GuildMember[] {
  const busca = normalizar(termo)

  if (!busca) {
    return membros.slice(0, limite)
  }

  const comeca: GuildMember[] = []
  const contem: GuildMember[] = []

  for (const membro of membros) {
    const nomes = [membro.user.username, membro.user.displayName, membro.nickname ?? ''].map(
      normalizar,
    )

    if (nomes.some((nome) => nome.startsWith(busca))) comeca.push(membro)
    else if (nomes.some((nome) => nome.includes(busca))) contem.push(membro)
  }

  return [...comeca, ...contem].slice(0, limite)
}

/**
 * Converte `@nome` em `<@id>` antes de enviar.
 *
 * Só troca o que casa por inteiro com alguém do servidor. Escrever "@todos" numa
 * frase, quando não existe ninguém com esse nome, continua sendo texto comum em
 * vez de virar uma menção quebrada.
 *
 * `escolhidos` guarda o que a pessoa selecionou na lista de sugestões, e tem a
 * palavra final. É o que resolve duas pessoas com o mesmo apelido no servidor:
 * o nome escrito é idêntico, mas só uma delas foi apontada.
 */
export function resolverMencoes(
  texto: string,
  membros: GuildMember[],
  escolhidos?: ReadonlyMap<string, string>,
): string {
  if (!texto.includes('@')) return texto

  const candidatos: { rotulo: string; userId: string }[] = []

  if (escolhidos) {
    for (const [rotulo, userId] of escolhidos) candidatos.push({ rotulo, userId })
  }
  for (const membro of membros) {
    for (const nome of new Set(
      [membro.nickname, membro.user.displayName, membro.user.username].filter(
        (n): n is string => Boolean(n),
      ),
    )) {
      candidatos.push({ rotulo: nome, userId: membro.userId })
    }
  }

  /*
    Do nome mais longo para o mais curto. Com "Ana" e "Ana Paula" no mesmo
    servidor, escrever "@Ana Paula" tem que casar a segunda; testando na ordem
    inversa, sobraria um " Paula" solto no meio da frase.

    A ordenação do JavaScript é estável, então em nomes de mesmo tamanho quem
    veio da lista de sugestões continua na frente.
  */
  candidatos.sort((a, b) => b.rotulo.length - a.rotulo.length)

  let resultado = ''
  let indice = 0

  while (indice < texto.length) {
    const caractere = texto[indice] as string
    const inicioValido = indice === 0 || /\s/.test(texto[indice - 1] as string)

    if (caractere === '@' && inicioValido) {
      const resto = texto.slice(indice + 1)
      const achado = candidatos.find(
        (candidato) => resto.startsWith(candidato.rotulo) && terminaLimpo(resto, candidato.rotulo),
      )

      if (achado) {
        resultado += `<@${achado.userId}>`
        indice += 1 + achado.rotulo.length
        continue
      }
    }

    resultado += caractere
    indice += 1
  }

  return resultado
}

/**
 * O nome precisa acabar onde a palavra acaba.
 *
 * Sem isso, num servidor onde existe "Ana" mas não "Anabela", escrever
 * "@Anabela" viraria uma menção à Ana seguida de "bela" solto.
 */
function terminaLimpo(resto: string, rotulo: string): boolean {
  const seguinte = resto[rotulo.length]
  return seguinte === undefined || !/[\p{L}\p{N}_]/u.test(seguinte)
}

function normalizar(valor: string): string {
  return valor
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}
